import type { ElementType, FC } from 'react';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import ForwardRoundedIcon from '@mui/icons-material/ForwardRounded';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import MoodRoundedIcon from '@mui/icons-material/MoodRounded';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PermContactCalendarRoundedIcon from '@mui/icons-material/PermContactCalendarRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import PresentToAllRoundedIcon from '@mui/icons-material/PresentToAllRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SignalWifiConnectedNoInternet4RoundedIcon from '@mui/icons-material/SignalWifiConnectedNoInternet4Rounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import SvgIcon from '@mui/material/SvgIcon';

import AccountFilled from './assets/account-filled.svg?react';
import Account from './assets/account.svg?react';
import ArrowLeft from './assets/arrow-left.svg?react';
import ArrowRight from './assets/arrow-right.svg?react';
import Bank from './assets/bank.svg?react';
import AmericanExpress from './assets/cc-amex.svg?react';
import DinersClub from './assets/cc-diners-club.svg?react';
import Discover from './assets/cc-discover.svg?react';
import Jcb from './assets/cc-jcb.svg?react';
import Mastercard from './assets/cc-mastercard.svg?react';
import Visa from './assets/cc-visa.svg?react';
import CircleClose from './assets/circle-close.svg?react';
import CourseFilled from './assets/courses-filled.svg?react';
import Course from './assets/courses.svg?react';
import Discount from './assets/discount.svg?react';
import Download from './assets/download.svg?react';
import Jobs from './assets/jobs.svg?react';
import LinearCheckMark from './assets/linear-checkmark.svg?react';
import MoodleFilled from './assets/moodle-filled.svg?react';
import Moodle from './assets/moodle.svg?react';
import OdtQuestionFilled from './assets/odt-question-filled.svg?react';
import OdtQuestion from './assets/odt-question.svg?react';
import PassFilled from './assets/pass-filled.svg?react';
import Pass from './assets/pass.svg?react';
import PlanFilled from './assets/plan-filled.svg?react';
import Plan from './assets/plan.svg?react';
import PrepFilled from './assets/prep-filled.svg?react';
import Prep from './assets/prep.svg?react';
import RatingFilled from './assets/rating-filled.svg?react';
import Rating from './assets/rating.svg?react';
import RewardsFilled from './assets/rewards-filled.svg?react';
import Rewards from './assets/rewards.svg?react';
import Support from './assets/support.svg?react';
import TrackTime from './assets/track-time.svg?react';

const unwrapDefault = <T,>(component: T): T => {
  return ((component as { default?: T }).default || component) as T;
};

const renderIcon = (Icon: unknown, props: SvgIconProps) => {
  const IconComponent = unwrapDefault(Icon) as ElementType<SvgIconProps>;

  return <IconComponent {...props} />;
};

export const AmericanExpressIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={AmericanExpress} {...props} />;
};
export const DinersClubIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={DinersClub} {...props} />;
};
export const DiscoverIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Discover} {...props} />;
};
export const JcbIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Jcb} {...props} />;
};
export const MastercardIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Mastercard} {...props} />;
};
export const VisaIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Visa} {...props} />;
};
export const BankIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Bank} {...props} />;
};

export const BankOutlinedIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(AccountBalanceRoundedIcon, props);
};

export const ThumbsUpIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ThumbUpAltRoundedIcon, props);
};
export const ThumbsDownIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ThumbDownRoundedIcon, props);
};

export const MailIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(MailOutlineRoundedIcon, props);
};

export const AddNoteIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(NoteAddOutlinedIcon, props);
};

export const ArticleIcon: FC<SvgIconProps> = (props) => {
  return (
    <SvgIcon {...props}>
      <path d="M19 5v14H5V5zm0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2" />
      <path d="M14 17H7v-2h7zm3-4H7v-2h10zm0-4H7V7h10z" />
    </SvgIcon>
  );
};

export const CoursesIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(BookmarkBorderRoundedIcon, props);
};

export const OdtQuestionIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={OdtQuestion} viewBox="0 0 36 37" {...props} />;
};

export const OdtQuestionFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={OdtQuestionFilled} viewBox="0 0 40 40" {...props} />;
};

export const DiscountIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Discount} viewBox="0 0 18 22" {...props} />;
};

export const SecurityIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(SecurityRoundedIcon, props);
};

export const AccountIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Account} viewBox="0 0 32 32" {...props} />;
};

export const RatingStarIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(StarRateRoundedIcon, props);
};

export const AddCircleIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(AddCircleOutlineRoundedIcon, props);
};

export const RemoveCircleIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(RemoveCircleOutlineRoundedIcon, props);
};

export const NoInternetIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(SignalWifiConnectedNoInternet4RoundedIcon, props);
};

export const OneOnOneSessionIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(SupervisorAccountRoundedIcon, props);
};

export const StartLessonIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(PresentToAllRoundedIcon, props);
};

export const SendIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(SendRoundedIcon, props);
};

export const RosterIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(FormatListBulletedRoundedIcon, props);
};

export const EmailIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(EmailRoundedIcon, props);
};

export const CheckCircleIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CheckCircleOutlineRoundedIcon, props);
};

export const DeleteIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(DeleteForeverRoundedIcon, props);
};

export const EditIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ModeEditRoundedIcon, props);
};

export const ContactCalendarIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(PermContactCalendarRoundedIcon, props);
};

export const CircularPlayIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(PlayCircleRoundedIcon, props);
};

export const HistoryIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(HistoryRoundedIcon, props);
};

export const FutureCourseIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ForwardRoundedIcon, props);
};

export const JobsIcon: FC<SvgIconProps> = (props) => {
  return <Jobs {...props} />;
};

export const DownloadIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(DownloadRoundedIcon, props);
};

export const ArrowForwardIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ArrowForwardRoundedIcon, props);
};

export const ArrowBackIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ArrowBackRoundedIcon, props);
};

export const LockedIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(LockRoundedIcon, props);
};

export const UnLockedIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(LockOpenRoundedIcon, props);
};

export const CloseIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CloseRoundedIcon, props);
};

export const AddIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(AddRoundedIcon, props);
};

export const CloudDownloadIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CloudDownloadRoundedIcon, props);
};

export const ReplayIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ReplayRoundedIcon, props);
};

export const HeadsetMicIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(HeadsetMicRoundedIcon, props);
};

export const SearchLenseIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(SearchRoundedIcon, props);
};

export const CircleCheckRoundedIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CheckCircleRoundedIcon, props);
};

export const InfoIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(InfoRoundedIcon, props);
};

export const ArrowUpIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(KeyboardArrowUpRoundedIcon, props);
};

export const ArrowDownIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(KeyboardArrowDownRoundedIcon, props);
};

export const ArrowRightIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(KeyboardArrowRightRoundedIcon, props);
};

export const ArrowLeftIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(KeyboardArrowLeftRoundedIcon, props);
};

export const SmileyIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(MoodRoundedIcon, props);
};

export const WarningIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(WarningRoundedIcon, props);
};

export const PhoneIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(PhoneRoundedIcon, props);
};

export const CircleCloseIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={CircleClose} viewBox="0 0 1024 1024" {...props} />;
};

export const ArrowLeftWBIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={ArrowLeft} viewBox="0 0 1024 1024" {...props} />;
};

export const InvoiceDownloadIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Download} viewBox="0 0 1052 1024" {...props} />;
};

export const ArrowRightWBIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={ArrowRight} viewBox="0 0 1052 1024" {...props} />;
};

export const StarBorderIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(StarBorderRoundedIcon, props);
};

export const CheckIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CheckRoundedIcon, props);
};

export const CancelCircularIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(CancelRoundedIcon, props);
};

export const FlagIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(FlagRoundedIcon, props);
};

export const VisibilityOnIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(VisibilityRoundedIcon, props);
};

export const VisibilityOffIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(VisibilityOffRoundedIcon, props);
};

export const MessageIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(ChatBubbleRoundedIcon, props);
};

export const LogoutIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(LaunchRoundedIcon, props);
};

export const WhyBridgeIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(HelpRoundedIcon, props);
};

export const CalenderIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(EventRoundedIcon, props);
};

export const NumberListingIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(FormatListNumberedRoundedIcon, props);
};

export const KnowledgeBaseIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(LibraryBooksRoundedIcon, props);
};

export const BillingIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(MonetizationOnRoundedIcon, props);
};

export const SupportIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Support} viewBox="0 0 32 32" {...props} />;
};

export const AccountFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={AccountFilled} viewBox="0 0 32 32" {...props} />;
};

export const ManualIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(MenuBookRoundedIcon, props);
};

export const MyTimeIcon: FC<SvgIconProps> = (props) => {
  return renderIcon(AccessTimeRoundedIcon, props);
};

export const CourseIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Course} viewBox="0 0 32 32" {...props} />;
};

export const CourseFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={CourseFilled} viewBox="0 0 32 32" {...props} />;
};

export const PassIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Pass} viewBox="0 0 32 32" {...props} />;
};

export const PassFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={PassFilled} viewBox="0 0 32 32" {...props} />;
};

export const PrepIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Prep} viewBox="0 0 32 32" {...props} />;
};

export const PrepFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={PrepFilled} viewBox="0 0 32 32" {...props} />;
};

export const PlanIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Plan} viewBox="0 0 32 32" {...props} />;
};

export const PlanFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={PlanFilled} viewBox="0 0 32 32" {...props} />;
};

export const RatingIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Rating} viewBox="0 0 32 32" {...props} />;
};

export const RatingFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={RatingFilled} viewBox="0 0 32 32" {...props} />;
};

export const TrackTimeIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={TrackTime} viewBox="0 0 32 32" {...props} />;
};

export const MoodleIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Moodle} viewBox="0 0 32 32" {...props} />;
};

export const MoodleFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={MoodleFilled} viewBox="0 0 32 32" {...props} />;
};

export const LinearCheckMarkIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={LinearCheckMark} viewBox="0 0 32 32" {...props} />;
};

export const RewardsIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={Rewards} viewBox="0 0 36 36" {...props} />;
};

export const RewardsFilledIcon: FC<SvgIconProps> = (props) => {
  return <SvgIcon component={RewardsFilled} viewBox="0 0 36 36" {...props} />;
};
